import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DocumentReasonDialogComponent } from '../../Dialogs/document-reason-dialog/document-reason-dialog.component';
import { CreditNoteDialogComponent } from '../../Dialogs/credit-note-dialog/credit-note-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { InvoiceDetailFacade } from '../../../../../Facades/Billing/invoice-detail.facade';
import { DATE_FORMAT, DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  CONFIRM_BUTTON,
  INVOICE_RESEND_CONSEQUENCE,
  INVOICE_RESEND_TITLE,
  INVOICE_UNCOLLECTIBLE_CONSEQUENCE,
  INVOICE_UNCOLLECTIBLE_TITLE,
} from '../../../../../Consts/billing-documents.const';
import {
  CREDIT_NOTE_REASON_LABEL,
  CREDIT_NOTE_STATUS_LABEL,
} from '../../../../../Enums/money-out.enum';
import { PAYMENT_STATUS_LABEL } from '../../../../../Enums/stripe-status.enum';
import { PRICE_COMPONENT_LABEL, PriceComponentType } from '../../../../../Enums/price-component-type.enum';
import {
  CreditNoteDialogResult,
  DocumentReasonDialogResult,
} from '../../../Interfaces/billing-dialog.interface';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-invoice-detail',
  standalone: true,
  imports: [
    RouterLink,
    DatePipe,
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    AvEmptyStateComponent,
    AvStatusChipComponent,
    TranslateModule,
  ],
  templateUrl: './invoice-detail.component.html',
  styleUrl: './invoice-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class InvoiceDetailComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(InvoiceDetailFacade);

  readonly dateFormat = DATE_FORMAT;
  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly componentLabelKeys = PRICE_COMPONENT_LABEL;
  readonly paymentStatusLabels = PAYMENT_STATUS_LABEL;
  readonly creditNoteStatusLabels = CREDIT_NOTE_STATUS_LABEL;
  readonly creditNoteReasonLabels = CREDIT_NOTE_REASON_LABEL;

  private readonly _params = toSignal(this._route.paramMap);

  readonly documentsLink = computed<string>(
    () => `/organizations/${this._params()?.get('id') ?? ''}/billing`,
  );

  constructor() {
    this._registerRouteParams();
  }

  paymentStatusLabel(status: string): string {
    return this.paymentStatusLabels[status] || status;
  }

  creditNoteStatusLabel(status: string | null): string {
    return status === null ? this.unresolved : this.creditNoteStatusLabels[status] || status;
  }

  creditNoteReasonLabel(reason: string | null): string {
    return reason === null ? this.unresolved : this.creditNoteReasonLabels[reason] || reason;
  }

  componentLabelKey(componentType: string | null): string {
    if (componentType === null) {
      return this.unresolved;
    }

    return this.componentLabelKeys[componentType as PriceComponentType] ?? componentType;
  }

  reload(): void {
    this.facade.reload();
  }

  openDocument(): void {
    this.facade.openDocument();
  }

  openResend(): void {
    const reference = this._dialog.open(DocumentReasonDialogComponent, {
      data: {
        title: INVOICE_RESEND_TITLE,
        consequence: INVOICE_RESEND_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        subjectLabel: this.facade.invoice()?.stripe_invoice_id ?? '',
        confirmLabel: CONFIRM_BUTTON,
        isReasonRequired: false,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: DocumentReasonDialogResult) => this._resend(result));
  }

  openUncollectible(): void {
    const reference = this._dialog.open(DocumentReasonDialogComponent, {
      data: {
        title: INVOICE_UNCOLLECTIBLE_TITLE,
        consequence: INVOICE_UNCOLLECTIBLE_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        subjectLabel: this.facade.invoice()?.stripe_invoice_id ?? '',
        confirmLabel: CONFIRM_BUTTON,
        isReasonRequired: true,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: DocumentReasonDialogResult) => this._markUncollectible(result));
  }

  openCreditNote(): void {
    const invoice = this.facade.invoice();

    if (invoice === null) {
      return;
    }

    const reference = this._dialog.open(CreditNoteDialogComponent, {
      data: {
        organizationName: this.facade.organizationName(),
        invoiceReference: invoice.stripe_invoice_id ?? invoice.id,
        currency: invoice.currency,
        creditableRemainder: invoice.creditable_remainder,
        isPaid: invoice.stripe_status === 'paid',
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: CreditNoteDialogResult) => this._issueCreditNote(result));
  }

  private _registerRouteParams(): void {
    effect(() => {
      this.facade.load(
        this._params()?.get('id') ?? '',
        this._params()?.get('invoiceId') ?? '',
      );
    });
  }

  private _resend(result?: DocumentReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.resend(result.reason);
  }

  private _markUncollectible(result?: DocumentReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.markUncollectible(result.reason);
  }

  private _issueCreditNote(result?: CreditNoteDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.issueCreditNote({
      amount: result.amount,
      reason: result.reason,
      refund: result.refund,
      note: result.note ?? undefined,
    });
  }
}
